import React from "react";
import { SparkleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { StyleSheet, View } from "react-native";

import { colors, radii, spacing, shadowStyles } from "../../../../styles/design-system";
import CustomText from "../../../common/general/CustomText/CustomText";

interface AiAssessmentSummaryProps {
  aiAssessmentSummary?: string | null;
  aiRecommendations?: string[] | null;
  aiNeedsIntervention?: boolean | null;
}

const AiAssessmentSummary: React.FC<AiAssessmentSummaryProps> = ({
  aiAssessmentSummary,
  aiRecommendations = [],
  aiNeedsIntervention = false,
}) => {
  if (!aiAssessmentSummary && aiRecommendations.length === 0) {
    return null;
  }

  const summary =
    aiRecommendations.length > 0
      ? `${aiAssessmentSummary}Recommendations: ${aiRecommendations.join(" ")}`
      : aiAssessmentSummary;
  return (
    <View style={[styles.container, shadowStyles.sm]}>
      <View style={styles.headerRow}>
        <CustomText
          text="AI Assessment & Recommendations"
          size="xl"
          weight="bold"
          useTranslationText={false}
          style={styles.title}
        />

        {aiNeedsIntervention ? (
          <View style={styles.interventionPill}>
            <WarningCircleIcon size={14} color={colors.error[700]} weight="regular" />
            <CustomText
              text="Needs Intervention"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.interventionText}
            />
          </View>
        ) : null}
      </View>

      {aiAssessmentSummary ? (
        <View style={styles.assessmentCard}>
          <View style={styles.assessmentHeader}>
            <SparkleIcon size={16} color={colors.accent.purple600} weight="regular" />
            <CustomText
              text="Overall Assessment"
              size="m"
              weight="bold"
              useTranslationText={false}
              style={styles.assessmentLabel}
            />
          </View>

          <CustomText text={summary} useTranslationText={false} style={styles.assessmentText} />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
    gap: spacing.lg,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    flexWrap: "wrap",
  },
  title: {
    color: colors.grey[900],
  },
  interventionPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: colors.error[50],
    borderRadius: radii.full,
    paddingHorizontal: spacing.xs,
    paddingVertical: 6,
  },
  interventionText: {
    color: colors.error[700],
  },
  assessmentCard: {
    backgroundColor: colors.grey[50],
    borderRadius: 28,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
    gap: spacing.sm,
  },
  assessmentHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  assessmentLabel: {
    color: colors.grey[900],
  },
  assessmentText: {
    color: colors.grey[700],
    lineHeight: 30,
  },
});

export default AiAssessmentSummary;
